import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { entityService, Entity } from '../services/entityService';

interface EntityContextType {
    currentEntity: Entity | null;
    availableEntities: Entity[];
    loading: boolean;
    error: string | null;
    setCurrentEntity: (entity: Entity | null) => Promise<void>;
    clearCurrentEntity: () => void;
    fetchAvailableEntities: () => Promise<void>;
}

const EntityContext = createContext<EntityContextType | undefined>(undefined);

export const useEntity = () => {
    const context = useContext(EntityContext);
    if (!context) {
        throw new Error('useEntity must be used within an EntityProvider');
    }
    return context;
};

interface EntityProviderProps {
    children: ReactNode;
}

export const EntityProvider: React.FC<EntityProviderProps> = ({ children }) => {
    const [currentEntity, setCurrentEntityState] = useState<Entity | null>(null);
    const [availableEntities, setAvailableEntities] = useState<Entity[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchAvailableEntities = useCallback(async () => {
        if (availableEntities.length > 0) return; // Only fetch if we don't have entities

        try {
            setLoading(true);
            setError(null);
            const entities = await entityService.getUserEntities();
            setAvailableEntities(entities);

            // If there's a stored entity ID, find and set it
            const storedEntityId = entityService.getCurrentEntity();
            if (storedEntityId) {
                const entity = entities.find(e => e.entidade_id === storedEntityId);
                if (entity) {
                    setCurrentEntityState(entity);
                }
            }
        } catch (err) {
            setError('Erro ao carregar entidades disponíveis');
            console.error('Error fetching entities:', err);
        } finally {
            setLoading(false);
        }
    }, [availableEntities.length]);

    const setCurrentEntity = useCallback(async (entity: Entity | null) => {
        try {
            // Optimistically update the UI
            setCurrentEntityState(entity);
            // Then update storage in the background
            if (entity) {
                await entityService.setCurrentEntity(entity.entidade_id);
            } else {
                // Clear localStorage when entity is set to null
                localStorage.removeItem('currentEntityId');
            }
        } catch (err) {
            // Revert on error
            setCurrentEntityState(currentEntity);
            setError('Erro ao definir a entidade atual');
            console.error('Error setting current entity:', err);
        }
    }, [currentEntity]);

    const clearCurrentEntity = useCallback(() => {
        localStorage.removeItem('currentEntityId');
        setCurrentEntityState(null);
    }, []);

    // Fetch entities only once when the provider mounts
    useEffect(() => {
        fetchAvailableEntities();
    }, [fetchAvailableEntities]);

    const value = useMemo(() => ({
        currentEntity,
        availableEntities,
        loading,
        error,
        setCurrentEntity,
        clearCurrentEntity,
        fetchAvailableEntities
    }), [
        currentEntity,
        availableEntities,
        loading,
        error,
        setCurrentEntity,
        clearCurrentEntity,
        fetchAvailableEntities
    ]);

    return (
        <EntityContext.Provider value={value}>
            {children}
        </EntityContext.Provider>
    );
}; 